import { SearchIndexClient, AzureKeyCredential, SearchOptions, SearchClient } from '@azure/search-documents';
import regexescape from 'regex-escape';


export type SearchServiceResponse<T> = T | [Error | any];

export interface ISearchService{
  endPoint: string;
  apiKey: string;
  indexClient: SearchIndexClient;
  searchClient: SearchClient<unknown>;
  getSingle<T = unknown>(id: string, ): Promise<SearchServiceResponse<T>>;
  getArray<T = unknown>(searchOptions: SearchOptions<never>, value?: string,): Promise<SearchServiceResponse<T>>;
}

// const TOP_AMOUNT = 100; // Maximum number of search results
// Indexes: https://portal.azure.com/#@crv4all.onmicrosoft.com/resource/subscriptions/618e5fc5-24f2-4a06-96f6-2596f9128d3e/resourceGroups/CRV-Siresearch-tst-weu/providers/Microsoft.Search/searchServices/srch-crv-siresearch-index-tst-weu/indexes
const NAME_INDEX = "bullinfo_aug2026_3";

export class AzureSearchService implements ISearchService {
  endPoint = process.env.VUE_APP_SEARCH_API_ENDPOINT as string;
  apiKey = process.env.VUE_APP_SEARCH_API_KEY as string;
  indexClient = new SearchIndexClient(this.endPoint, new AzureKeyCredential(this.apiKey));
  searchClient = this.indexClient.getSearchClient(NAME_INDEX);

  async getSingle<T = unknown>(id: string): Promise<SearchServiceResponse<T>> {
    // We had some issues with dashes in the document ID, so we remove them here. EMP-551
    const idFixed = id.slice(0, 15).replace("-", "") + id.slice(15);
    const data = await this.searchClient.getDocument(idFixed) as T;
    return data;
  }

  async getArray<T = unknown>(searchOptions: SearchOptions<never>, value?: string | undefined): Promise<SearchServiceResponse<T>> {
    value = regexescape(value)

    if (value === undefined) {
      value = "";
    }

    const topAmountThreshold = 5000;

    searchOptions.top = searchOptions.top ?? topAmountThreshold; // Default Top-amount Threshold 
    const searchTerm = this.constructProperSearchTerm(value);
    const searchResults = await this.searchClient.search(searchTerm, searchOptions);

    if (value !== "" && value !== "*" && searchResults.count !== undefined && searchResults.count > topAmountThreshold) {
      console.error(`Too many results: ${searchResults.count}`);
      return [new Error(`Too many results: ${searchResults.count}`)];
    }

    const jsonArray: T[] = [];
    let i = 0;
    for await (const result of searchResults.results) {
      const jsonObject = `${JSON.stringify(result.document)}`;
      const jsonGeneric = JSON.parse(jsonObject) as T;
      jsonArray[i] = jsonGeneric;
      i++;
    }

    if (searchOptions.top > 1) {
      // don't show on initial load to get the amount of results
      //console.log("\"" + searchTerm + "\" " + "count: " + jsonArray.length); 
    }
    return jsonArray as unknown as T;
  }

  /**
   * This method modifies the search-string so that it retrieves correct results.
   * 
   * The AI search service uses a search-analyzer and a index-analyzer.
   * The index-analyzer processes the records in the index, it tokenizes the searchable fields.
   * This means the fields are split in tokens according to the contract of the index-analyzer.
   * 
   * The search-analyzer splits the search-string into smaller pieces. For each piece the index is queried.
   * In this service searchMode = all is used, so all pieces of the search-string must match.
   * https://crv4all.atlassian.net/wiki/spaces/S2/pages/4914675714/Azure+AI+search
   * @param value
   */
  constructProperSearchTerm(value: string){
    return value.replace(/[^a-zA-Z0-9]/g, ' ').trim() + "*";
  }
}
